import {
  Box,
  Text,
  Grid,
  GridItem,
  Stack,
  Center,
  Flex,
  useToast,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";
import { usePaymentLinkExpiry } from "../../hooks/usePaymentLinkExpiry";
import { usePaymentStatusPolling } from "../../hooks/usePaymentStatusPolling";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in"; // API base URL

const DetailsPage = () => {
  const { id } = useParams(); // booking ID from URL
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const [paymentLink, setPaymentLink] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentLinkId, setPaymentLinkId] = useState(null);
  const [balancePaymentLinkSentTime, setBalancePaymentLinkSentTime] = useState(null);
  const { isExpired: isBalanceLinkExpired, formattedTime: balanceLinkTime } = usePaymentLinkExpiry(balancePaymentLinkSentTime);

  const fetchBooking = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("prima_session");
      if (!token) throw new Error("Auth token missing");

      const res = await axios.get(`${SERVER}/management/booking`, {
        params: { id },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (res.data) {
        const booking = res.data.data || res.data;
        console.log("Fetched booking details in detail page:", booking);
        console.log("Payment data:", booking.payments);
        console.log("First payment:", booking.payments?.[0]);
        setData(booking);

        // Check if there's an active pending balance payment and restore its timestamp
        const pendingBalancePayment = booking.payments?.find(
          p => p.status === "Pending" && p.type === "Balance" && p.paymentLinkIdField
        );

        if (pendingBalancePayment?.createdAt) {
          const sentAt = new Date(pendingBalancePayment.createdAt);
          setBalancePaymentLinkSentTime(sentAt);
          console.log("Restored balance payment link sent time:", sentAt);
        }
      } else {
        throw new Error("Booking not found");
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Error fetching booking",
        description: err?.response?.data?.message || err.message,
        status: "error",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooking();
  }, [id, toast]);

  // Get pending payment link IDs for polling
  const pendingPaymentLinkIds = data?.payments
    ?.filter(p => p.status === "Pending" && p.paymentLinkIdField)
    .map(p => p.paymentLinkIdField) || [];

  // Auto-poll payment status using custom hook
  usePaymentStatusPolling({
    paymentLinkIds: pendingPaymentLinkIds,
    enabled: pendingPaymentLinkIds.length > 0,
    onPaymentDetected: async () => {
      toast({
        title: "Payment Received!",
        description: "Payment has been successfully processed.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh booking data
      const token = localStorage.getItem("prima_session");
      const bookingRes = await axios.get(`${SERVER}/management/booking`, {
        params: { id },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(bookingRes.data.data || bookingRes.data);
    },
  });

  const handlePayRemaining = async () => {
    try {
      // Check if a balance payment link is already active
      if (balancePaymentLinkSentTime && !isBalanceLinkExpired) {
        const expiryTime = new Date(
          balancePaymentLinkSentTime.getTime() + 16 * 60 * 1000
        );
        const formattedExpiry = expiryTime.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        // toast({
        //   title: "Payment Link Already Active",
        //   description: `A balance payment link is already active. You can resend after it expires at ${formattedExpiry}. Time remaining: ${balanceLinkTime}`,
        //   status: "warning",
        //   duration: 2000,
        //   isClosable: true,
        // });
        return;
      }

      const token = localStorage.getItem("prima_session");

      const res = await axios.post(
        `${SERVER}/payments/send-balance`,
        {
          bookingId: data._id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPaymentLink(res.data.paymentLink);
      setQrCode(res.data.qrCode);
      setPaymentStatus("Pending");
      setPaymentLinkId(res.data.paymentLinkId);

      // Set balance payment link sent time for expiry tracking
      setBalancePaymentLinkSentTime(new Date());

      toast({
        title: "Payment link sent to customer",
        status: "success",
      });

      // Calculate expiry time (16 minutes from now)
      const expiryTime = new Date(Date.now() + 16 * 60 * 1000);
      const formattedExpiry = expiryTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      // toast({
      //   title: "Link Expires Soon",
      //   description: `Payment link will expire at ${formattedExpiry} (16 minutes from now). You can resend after it expires.`,
      //   status: "info",
      //   duration: 5000,
      //   isClosable: true,
      // });

      // Refresh booking data to start polling the new pending payment
      await fetchBooking();
    } catch (err) {
      toast({
        title: "Payment failed",
        description: err?.response?.data?.msg || err.message,
        status: "error",
      });
    }
  };

  if (loading) return <Loading />;

  if (!data)
    return (
      <Center py={20}>
        <Text color="red.500">No booking found</Text>
      </Center>
    );

  const totalAmount = data?.fareDetails?.total || 0;

  const paidAmount =
    data?.payments?.reduce((sum, p) => {
      if (p.status === "Paid") return sum + (p.amount || 0);
      return sum;
    }, 0) || 0;

  const remainingAmount = Math.max(0, totalAmount - paidAmount);

  return (
    <Stack spacing={6} p={6}>
      {/* Breadcrumb */}
      <BreadCrumbMaker
        items={[
          { title: "Booking", link: "/bookings" },
          { title: "View Booking", link: `/booking/${id}`, on: true },
        ]}
      />

      {/* Booking Card */}
      <Box bg="white" borderRadius="lg" border="1px solid #E5E7EB" p={6}>
        <Text fontSize="lg" fontWeight="600" mb={5}>
          Booking Information
        </Text>

        <Grid templateColumns="repeat(3, 1fr)" gap={6}>
          <GridItem>
            <LabelValue label="Booking ID" value={data.bookingID || data.customBookingID || data._id || "-"} />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Customer Name"
              value={data.customer?.name || data.customerName || "-"}
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Contact Number"
              value={data.customer?.mobile || data.mobileNo || "-"}
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Passengers"
              value={
                data.passengers && data.passengers.length > 0
                  ? data.passengers.map(p => p.name).join(", ")
                  : "-"
              }
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Date and Time"
              value={
                data.dateTime
                  ? new Date(data.dateTime).toLocaleString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })
                  : data.date || "-"
              }
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Original Price"
              value={
                data.fareDetails?.baseFare
                  ? `₹${data.fareDetails.baseFare}`
                  : "-"
              }
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Discount"
              value={
                data.fareDetails?.baseFare && data.fareDetails?.total
                  ? `₹${data.fareDetails.baseFare - data.fareDetails.total}`
                  : "-"
              }
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Final Price"
              value={
                data.fareDetails?.total ? `₹${data.fareDetails.total}` : "-"
              }
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Amount Paid"
              value={
                data.payments && data.payments.length > 0
                  ? `₹${data.payments.reduce((sum, p) => sum + (p.amount || 0), 0)}`
                  : "₹0"
              }
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Remaining Amount"
              value={
                data.fareDetails?.total && data.payments
                  ? `₹${Math.max(
                    0,
                    data.fareDetails.total -
                    data.payments.reduce((sum, p) => sum + (p.amount || 0), 0)
                  )}`
                  : `₹${data.fareDetails?.total || 0}`
              }
            />
          </GridItem>
          <GridItem colSpan={2}>
            <LabelValue
              label="Transaction IDs"
              value={
                data.payments && data.payments.length > 0
                  ? data.payments.map((p, idx) => (
                    <Text key={idx} fontSize="sm">
                      {p.type === "Token" ? "Token" : "Balance"}: {p.paymentUniqueId || p.paymentLinkIdField || "-"}
                      {p.status === "Pending" && " (Pending)"}
                    </Text>
                  ))
                  : "-"
              }
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Car Model"
              value={data.vehicleSegment?.segmentName || data.car || "-"}
            />
          </GridItem>

          <GridItem colSpan={2}>
            <LabelValue
              label="Pickup Location"
              value={
                typeof (
                  data.pickupLocation ??
                  data.leadID?.fromLocation ??
                  data.pickUp
                ) === "object"
                  ? (
                    data.pickupLocation ??
                    data.leadID?.fromLocation ??
                    data.pickUp
                  )?.address ||
                  JSON.stringify(
                    data.pickupLocation ??
                    data.leadID?.fromLocation ??
                    data.pickUp,
                  )
                  : (data.pickupLocation ??
                    data.leadID?.fromLocation ??
                    data.pickUp ??
                    "-")
              }
            />
          </GridItem>
          <GridItem>
            <LabelValue
              label="Drop Location"
              value={
                typeof (
                  data.dropLocation ??
                  data.leadID?.toLocation ??
                  data.destination
                ) === "object"
                  ? (
                    data.dropLocation ??
                    data.leadID?.toLocation ??
                    data.destination
                  )?.address ||
                  JSON.stringify(
                    data.dropLocation ??
                    data.leadID?.toLocation ??
                    data.destination,
                  )
                  : (data.dropLocation ??
                    data.leadID?.toLocation ??
                    data.destination ??
                    "-")
              }
            />
          </GridItem>
        </Grid>

        {/* Notes */}
        {data.notes && (
          <Box mt={6}>
            <Text fontSize="sm" fontWeight="600" mb={1}>
              Notes
            </Text>
            <Box
              border="1px solid #E5E7EB"
              p={3}
              borderRadius="md"
              fontSize="sm"
              bg="gray.50"
            >
              {data.notes}
            </Box>
          </Box>
        )}
      </Box>

      {/* Payment Section */}
      <Box
        bg="white"
        borderRadius="lg"
        border="1px solid #E5E7EB"
        p={6}
      >
        <Text fontSize="lg" fontWeight="600" mb={4}>
          Payment
        </Text>

        {remainingAmount === 0 ? (
          <Text color="green.500" fontWeight="600">
            Full payment completed
          </Text>
        ) : (
          <>
            <Text mb={3}>
              Remaining Amount: <b>₹{remainingAmount}</b>
            </Text>

            <Flex gap={4} align="center">
              <Box
                bg="blue.500"
                color="white"
                px={4}
                py={2}
                borderRadius="md"
                cursor={balancePaymentLinkSentTime && !isBalanceLinkExpired ? "not-allowed" : "pointer"}
                isDisabled={balancePaymentLinkSentTime && !isBalanceLinkExpired}
                onClick={handlePayRemaining}
                style={{
                  opacity:
                    balancePaymentLinkSentTime && !isBalanceLinkExpired ? 0.6 : 1,
                }}
              >
                Pay Remaining Amount
              </Box>
              {!isBalanceLinkExpired && balancePaymentLinkSentTime && (
                <Text fontSize="sm" fontWeight="600" color="orange.600">
                  Payment Link already sent, Expires in: {balanceLinkTime}
                </Text>
              )}
            </Flex>

            {paymentLink && (
              <Stack spacing={3} mt={4}>
                <Text>
                  Status:{" "}
                  <b style={{ color: paymentStatus === "Paid" ? "green" : "orange" }}>
                    {paymentStatus}
                  </b>
                </Text>

                <a href={paymentLink} target="_blank" rel="noreferrer">
                  <Text color="blue.500" textDecoration="underline">
                    Open Payment Link
                  </Text>
                </a>

                {qrCode && (
                  <Box>
                    <img src={qrCode} alt="QR Code" width="180" />
                  </Box>
                )}

              </Stack>
            )}
          </>
        )}
      </Box>

    </Stack>
  );
};

// Label + Value Component
const LabelValue = ({ label, value }) => (
  <Box>
    <Text fontSize="sm" color="gray.500" fontWeight="500">
      {label}
    </Text>
    <Text fontSize="md" fontWeight="500" mt={1}>
      {value}
    </Text>
  </Box>
);

export default DetailsPage;
